import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { IconButton } from '@/components/icon-button/IconButton'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { OverflowMenu, ArrowUpRight, Edit, MoveToFolder, TrashDelete, FileDownload } from '@/assets/icons'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'

interface FileOverflowMenuProps {
  /** On download */
  onDownload?: () => void
  /** Optional rename functionality */
  onRename?: () => void
  /** On delete */
  onDelete?: () => void
  /** On move */
  onMove?: () => void
  /** Disable the menu */
  disabled?: boolean
  /** Set with onOpen to show the provider action. */
  externalProvider?: ExternalStorageProvider | null
  onOpen?: () => void
  /** Static note shown below the actions, e.g. "Read only. Managed by Box." for synced files. */
  footerNote?: string
}

/**
 * FileOverflowMenu
 *
 * Dropdown menu for file actions (download, rename, move, delete, open in the owning DMS).
 */
export function FileOverflowMenu({ onDownload, onRename, onDelete, onMove, disabled = false, externalProvider, onOpen, footerNote }: FileOverflowMenuProps) {
  const canOpenInProvider = Boolean(onOpen && externalProvider)
  const hasActions = Boolean(onDownload || onRename || onDelete || onMove || canOpenInProvider)

  if (!hasActions && !footerNote) {
    return null
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger disabled={disabled} asChild>
        <IconButton icon={<OverflowMenu />} variant="plain" color="neutral" aria-label="File actions" disabled={disabled} />
      </DropdownMenu.Trigger>

      <MenuContent align="end" alignOffset={-3} side="bottom">
        {hasActions && (
          <MenuItemGroup>
            {onDelete && <MenuItem label="Delete" icon={TrashDelete} onSelect={onDelete} variant="destructive" />}
            {onRename && <MenuItem label="Rename" icon={Edit} onSelect={onRename} variant="neutral" />}
            {onDownload && <MenuItem label="Download" icon={FileDownload} onSelect={onDownload} variant="neutral" />}
            {onOpen && externalProvider && (
              <MenuItem
                label={`Open in ${PROVIDER_COMPACT_DISPLAY_NAME[externalProvider]}`}
                icon={PROVIDER_ICON_COMPONENTS[externalProvider]}
                trailingIcon={ArrowUpRight}
                onSelect={onOpen}
                variant="neutral"
              />
            )}
            {onMove && <MenuItem label="Move" icon={MoveToFolder} onSelect={onMove} variant="neutral" />}
          </MenuItemGroup>
        )}
        {footerNote && (
          <>
            {hasActions && <MenuSeparator />}
            <div className="px-3 py-2 text-xs text-brand-neutral-600">{footerNote}</div>
          </>
        )}
      </MenuContent>
    </DropdownMenu.Root>
  )
}
